<template>
  <div class="card" :class="shadow ? 'card-shadow' : ''" @mouseover="clearTimer" @mouseleave="onMouseLeave">
    <div class="card-header">
      <img class="card-icon" :src="icon" alt="" srcset="" />
      <div class="card-title">
        {{ title }}
        <span class="card-elapseTime">
          {{ elapseTime }}
        </span>
      </div>
      <span
        v-if="showClose"
        class="icon-close"
        @click="
          (e) => {
            e.stopPropagation()
            onCloseIconClick()
          }
        "
      ></span>
    </div>
    <div class="card-body">
      <slot></slot>
    </div>
    <footer>
      <slot name="footer"></slot>
    </footer>
  </div>
</template>

<script lang="ts" setup>
  import type { PropType } from 'vue'
  import { onBeforeUnmount, onMounted, ref, watch } from 'vue'
  import { toElapseTime } from '@/core/Utils'
  import '../styles/global.styl'
  import type { NoticeDataType } from '@/core/Notification/NotificationManager'
  import { debounce, noop } from 'lodash'

  const DURATION_AUTO_CLOSE = 5000
  const REFRESH_TIME_IN_MS = 1000 * 60
  const timer = ref()

  const props = defineProps({
    noticeDetail: {
      type: Object as PropType<NoticeDataType>,
      default: null,
    },
    icon: {
      type: String,
      default: '',
    },
    title: {
      type: String,
      default: '',
    },
    showClose: {
      type: Boolean,
      default: true,
    },
    autoClose: {
      type: Function,
      default: noop,
    },
    shadow: {
      type: Boolean,
      default: false,
    },
    onCloseIconClick: {
      type: Function as PropType<() => void>,
      default: noop,
    },
  })

  watch(
    () => props.noticeDetail.id,
    (newValue) => {
      if (newValue) {
        clearTimer()
        close()
      }
    }
  )

  const elapseTime = ref(toElapseTime(props.noticeDetail.timestamp))
  const refreshElapseTime = debounce(
    () => {
      elapseTime.value = toElapseTime(props.noticeDetail.timestamp)
    },
    REFRESH_TIME_IN_MS,
    { maxWait: REFRESH_TIME_IN_MS }
  )

  const computeElapseTime = () => {
    refreshElapseTime()
    requestAnimationFrame(computeElapseTime)
  }

  requestAnimationFrame(computeElapseTime)

  const clearTimer = () => {
    if (timer.value) clearTimeout(timer.value)
  }

  const close = () => {
    timer.value = setTimeout(() => {
      props.autoClose?.()
    }, DURATION_AUTO_CLOSE)
  }

  const onMouseLeave = () => {
    close()
  }

  onMounted(() => {
    close()
  })

  onBeforeUnmount(() => {
    clearTimer()
  })
</script>
<style scoped lang="stylus">

  .card-shadow
    box-shadow 0px 4px 20px rgba(16, 22, 82, 0.04)
  .card
    box-sizing border-box
    padding 18px 16px
    background #ffffff
    border 1px solid rgba(235, 236, 240, 0.6)
    border-radius 12px
    margin-bottom 12px
    overflow hidden
    .card-body
      flex-align-center()
      padding 12px 0
      font-weight bolder_font_weight
      font-size 14px
      line-height 20px
      color default_color
    .card-header
      flex-align-center(space-between)
      font-weight default_font_weight
      font-size 12px
      line-height 16px
      color default_color
      .card-icon
        rect(16px)
        margin-right 8px
      .card-title
        flex 1
        .card-elapseTime
          color #b3b3b3
          margin-left 8px
      .icon-close
        rect(20px)
        display inline-block
        cursor pointer
        background url('@assets/svgs/icon-search-close-default.svg')
        background-size 20px
        &:hover
          background url('@assets/svgs/icon-search-close-hover.svg')
    footer
      display flex
      justify-content flex-end
      :deep(.ant-btn)
        border-radius 6px
        color active_color
        border none
        font-weight bolder_font_weight
        font-size 14px
        padding 2px 12px
        background rgba(45, 64, 233, 0.15)
        height 28px
        &:hover
          background linear-gradient(0deg, rgba(0, 0, 0, 0.05), rgba(0, 0, 0, 0.05)), rgba(45, 64, 233, 0.15)

  @import '../styles/h5Adaptive/NotificationCenter.styl'
</style>
